import { type ReactNode } from 'react'

import { Button } from '@/components/button/button'
import { Card, CardContent } from '@/components/card/card'
import { Form, FormField, FormItem } from '@/components/form/form'
import { Label } from '@/components/label/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/sheet/sheet'
import { Switch } from '@/components/switch/switch'

import { type ClientPortalOption } from '@/lib/queries/case-dashboard/types'
import { cn } from '@/lib/utils'

import { type ClientPortalEoirOptionForm } from './types'
import { usePortalControlSheet } from './use-portal-control-sheet'

export interface PortalControlSheetProps {
  isOpen: boolean
  caseNumber?: string
  defaultPortalOption?: ClientPortalOption
  isLoadingSaveButton?: boolean
  onChangeOpen: (open: boolean) => void
  onClickSaveButton: (data: ClientPortalEoirOptionForm) => void
}

export const PortalControlSheet = ({
  isOpen,
  caseNumber,
  defaultPortalOption,
  isLoadingSaveButton,
  onChangeOpen,
  onClickSaveButton,
}: PortalControlSheetProps) => {
  const { form, sheetSide, isDisabledSaveButton } = usePortalControlSheet({
    isOpen,
    defaultPortalOption,
  })

  const alienNumber = `A-${caseNumber}`

  return (
    <Sheet open={isOpen} onOpenChange={onChangeOpen}>
      <SheetContent side={sheetSide}>
        <SheetHeader>
          <SheetDescription className={cn('sr-only')}>
            You can customize client portal here. Turn on or off each UIs of your choice.
          </SheetDescription>
        </SheetHeader>
        <SheetHeader>
          <SheetTitle className={cn('text-2xl font-semibold text-foreground')}>Customize client portal</SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form className={cn('mt-[60px]')} onSubmit={form.handleSubmit(onClickSaveButton)}>
            <p className={cn('text-lg font-semibold text-foreground')}>{alienNumber}</p>

            <Card className={cn('mt-3')}>
              <CardContent className={cn('px-4 py-5')}>
                <div className={cn('flex flex-col gap-y-5')}>
                  <FormField
                    control={form.control}
                    name="alertSchedule"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          id="other-critical-dates"
                          label="Other Critical Dates"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <p className={cn('mt-5', 'text-base text-foreground')}>General</p>
            <Card className={cn('mt-3')}>
              <CardContent className={cn('px-4 py-5')}>
                <FormField
                  control={form.control}
                  name="isDefaultSetting"
                  render={({ field }) => (
                    <SwitchRow
                      id="default-setting"
                      label={
                        <>
                          Save above settings as default <br />
                          for all cases
                        </>
                      }
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </CardContent>
            </Card>

            <Button
              variant="default"
              className={cn('mt-4', 'w-full')}
              disabled={isDisabledSaveButton}
              loading={isLoadingSaveButton}
            >
              Save
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

const SwitchRow = ({
  id,
  label,
  checked = false,
  onCheckedChange,
}: {
  id: string
  label: ReactNode
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) => {
  return (
    <div className={cn('flex items-center justify-between')}>
      <Label htmlFor={id} className={cn('leading-5')}>
        {label}
      </Label>
      <Switch id={id} className={cn('ml-6')} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  )
}
